import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import React from 'react';
import Form, { FormElement } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import OptionList from '@dashboard/components/Formik/components/OptionList';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { formatPriceInput } from '@dashboard/services/formatting';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import STRIPE_PAYMENT_METHODS from '@dashboard/enums/stripe-payment-methods';
import usePage from '@dashboard/hooks/usePage';

const paymentMethods = [
  { value: STRIPE_PAYMENT_METHODS.AFFIRM, name: 'Affirm' },
  { value: STRIPE_PAYMENT_METHODS.AFTERPAY_CLEARPAY, name: 'Afterpay & Clearpay' },
  { value: STRIPE_PAYMENT_METHODS.ALIPAY, name: 'Alipay' },
  { value: STRIPE_PAYMENT_METHODS.AMAZON_PAY, name: 'Amazon Pay' },
  { value: STRIPE_PAYMENT_METHODS.AU_BECS_DEBIT, name: 'BECS Direct Debit' },
  { value: STRIPE_PAYMENT_METHODS.BACS_DEBIT, name: 'BACS Direct Debit' },
  { value: STRIPE_PAYMENT_METHODS.BANCONTACT, name: 'Bancontact' },
  { value: STRIPE_PAYMENT_METHODS.BLIK, name: 'Blik' },
  { value: STRIPE_PAYMENT_METHODS.BOLETO, name: 'Boleto' },
  { value: STRIPE_PAYMENT_METHODS.ACSS_DEBIT, name: 'Canadian pre-authorized debit payment' },
  { value: STRIPE_PAYMENT_METHODS.CARD, name: 'Card' },
  { value: STRIPE_PAYMENT_METHODS.CASHAPP, name: 'Cash App' },
  { value: STRIPE_PAYMENT_METHODS.EPS, name: 'EPS' },
  { value: STRIPE_PAYMENT_METHODS.FPX, name: 'FPX' },
  { value: STRIPE_PAYMENT_METHODS.GIROPAY, name: 'Giropay' },
  { value: STRIPE_PAYMENT_METHODS.GRABPAY, name: 'GrabPay' },
  { value: STRIPE_PAYMENT_METHODS.IDEAL, name: 'iDEAL' },
  { value: STRIPE_PAYMENT_METHODS.KLARNA, name: 'Klarna' },
  { value: STRIPE_PAYMENT_METHODS.LINK, name: 'Link' },
  { value: STRIPE_PAYMENT_METHODS.MOBILEPAY, name: 'MobilePay' },
  { value: STRIPE_PAYMENT_METHODS.MULTIBANCO, name: 'Multibanco' },
  { value: STRIPE_PAYMENT_METHODS.OXXO, name: 'Oxxo' },
  { value: STRIPE_PAYMENT_METHODS.P24, name: 'P24' },
  { value: STRIPE_PAYMENT_METHODS.PAYNOW, name: 'PayNow' },
  { value: STRIPE_PAYMENT_METHODS.PROMPTPAY, name: 'PromptPay' },
  { value: STRIPE_PAYMENT_METHODS.REVOLUT_PAY, name: 'Revolut Pay' },
  { value: STRIPE_PAYMENT_METHODS.SEPA_DEBIT, name: 'SEPA Direct Debit' },
  { value: STRIPE_PAYMENT_METHODS.SOFORT, name: 'Sofort' },
  { value: STRIPE_PAYMENT_METHODS.SWISH, name: 'Swish' },
  { value: STRIPE_PAYMENT_METHODS.TWINT, name: 'TWINT' },
  { value: STRIPE_PAYMENT_METHODS.US_BANK_ACCOUNT, name: 'ACH Direct Debit' },
  { value: STRIPE_PAYMENT_METHODS.WECHAT_PAY, name: 'WeChat Pay' },
  { value: STRIPE_PAYMENT_METHODS.ZIP, name: 'Zip' },
];

function getPageFees(page) {
  if (!page?.fees || page.fees.length === 0) {
    return [];
  }

  return page.fees.map(fee => ({
    ...fee,
    type: fee.amount ? 'amount' : fee.percentage ? 'percentage' : 'amount', // fallback to amount
    amount: fee.amount
      ? formatPriceForInput({
          amount: fee.amount,
          currency: page.product?.currency || page.eventDetails?.currency,
          style: 'decimal',
        })
      : '',
    percentage: fee.percentage ? fee.percentage : '',
  }));
}

const FeesForm = ({ onChange }) => {
  const { page, updatePage } = usePage();

  return (
    <Formik
      initialValues={{
        fees: getPageFees(page),
      }}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const body = {
          fees: values.fees.map(fee => ({
            ...fee, // the entire object is sent to the backend so it includes _id and stripeProductId to prevent recreating it
            amount: fee.type === 'amount' ? formatPriceInput(fee.amount) : null,
            percentage: fee.type === 'percentage' ? fee.percentage : null,
          })),
        };

        const { data, error } = await updatePage.mutateAsync(body);

        if (data) {
          if (onChange) {
            onChange(data);
          }

          actions.setStatus('');
          actions.resetForm({
            values: {
              fees: getPageFees(data),
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, handleSubmit, setFieldValue, resetForm }) => (
        <Form onSubmit={handleSubmit}>
          <FormElement>
            <OptionList
              name="fees"
              options={values.fees}
              onChange={options => setFieldValue('fees', options)}
              values={values}
              optionShape={{
                name: '',
                type: 'amount',
                amount: '',
                percentage: '',
                multiplyByQuantity: false,
                multiplyByTickets: false,
                applyToSpecificPaymentMethods: false,
                paymentMethods: [],
              }}
              addOptionLabel="+ Add fee"
              deleteOptionLabel="Delete fee?"
              fields={({ index }) => [
                {
                  name: 'name',
                  label: 'Label',
                  type: 'text',
                  required: true,
                },
                {
                  type: 'columns',
                  columns: 2,
                  fields: [
                    {
                      name: 'type',
                      type: 'select',
                      label: 'Type',
                      options: [
                        { value: 'amount', label: 'Fixed amount' },
                        { value: 'percentage', label: 'Percentage' },
                      ],
                    },
                    {
                      name: 'amount',
                      label: 'Fee amount',
                      type: 'string',
                      visible: values.fees[index].type === 'amount',
                      required: values.fees[index].type === 'amount',
                    },
                    {
                      name: 'percentage',
                      label: 'Fee percentage',
                      type: 'string',
                      visible: values.fees[index].type === 'percentage',
                      required: values.fees[index].type === 'percentage',
                    },
                  ],
                },
                {
                  name: 'multiplyByQuantity',
                  tooltip:
                    'Add a quantity field to multiply the setup fee by the selected quantity',
                  label: 'Multiply by quantity',
                  type: 'checkbox',
                  visible:
                    page.type === 'checkout' &&
                    page.product.type === 'charge' &&
                    values.fees[index].type === 'amount',
                  reduceMargin: true,
                },
                {
                  name: 'multiplyByTickets',
                  label: 'Multiply by tickets',
                  type: 'checkbox',
                  visible: page.type === 'event' && values.fees[index].type === 'amount',
                  reduceMargin: true,
                },
                {
                  name: 'applyToSpecificPaymentMethods',
                  label: 'Apply to specific payment methods',
                  type: 'checkbox',
                },
                {
                  name: 'paymentMethods',
                  placeholder: 'All payment methods',
                  type: 'listbox',
                  options: paymentMethods,
                  multiple: true,
                  inset: true,
                  visible: values.fees[index].applyToSpecificPaymentMethods === true,
                },
              ]}
            />
          </FormElement>

          <FormElement>{status && status}</FormElement>

          {dirty && (
            <FormElement className="flex justify-end">
              <FormSubmit>
                <FormSubmit.State visible={!!dirty} />
                <FormSubmit.Cancel
                  onClick={() => {
                    resetForm();
                  }}
                />
                <FormSubmit.Button>Save</FormSubmit.Button>
              </FormSubmit>
            </FormElement>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default FeesForm;
